import { CloudUpload } from 'lucide-react';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Dropzone, { type Accept } from 'react-dropzone';
import { cn } from 'v5/platform/dom/cn';
import { getFilesFromClipboardData } from 'v5/platform/dom/getFilesFromClipboardData';

export type FileUploadDropzoneProps = {
  onDrop: (files: File[]) => void;
  multiple?: boolean;
  accept?: Accept | string;
  maxSize?: number;
  disabled?: boolean;
  className?: string;
  enablePaste?: boolean;
  /** Paste on the dropzone when focused, or anywhere while mounted (e.g. open dialog). */
  pasteScope?: 'container' | 'window';
  title?: ReactNode;
  description?: ReactNode;
  compact?: boolean;
};

function resolveAcceptLabel(
  accept: Accept | string | undefined,
  acceptImagesLabel: string
): string | null {
  if (!accept) {
    return null;
  }
  if (typeof accept === 'string') {
    return accept.includes('image/*') ? acceptImagesLabel : null;
  }
  if ('image/*' in accept) {
    return acceptImagesLabel;
  }
  return null;
}

export function FileUploadDropzone({
  onDrop,
  multiple = true,
  accept,
  maxSize = 100 * 1024 * 1024,
  disabled = false,
  className,
  enablePaste = true,
  pasteScope = 'window',
  title,
  description,
  compact = false
}: FileUploadDropzoneProps) {
  const { t } = useTranslation('patterns');
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0 || disabled) {
        return;
      }
      onDrop(multiple ? files : files.slice(0, 1));
    },
    [disabled, multiple, onDrop]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent | React.ClipboardEvent) => {
      if (!enablePaste || disabled) {
        return;
      }

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, [contenteditable="true"]')
      ) {
        return;
      }

      const files = getFilesFromClipboardData(event.clipboardData);
      if (files.length === 0) {
        return;
      }

      event.preventDefault();
      handleFiles(files);
    },
    [disabled, enablePaste, handleFiles]
  );

  useEffect(() => {
    if (!enablePaste || pasteScope !== 'window' || disabled) {
      return;
    }

    const listener = (event: ClipboardEvent) => handlePaste(event);
    window.addEventListener('paste', listener);
    return () => window.removeEventListener('paste', listener);
  }, [disabled, enablePaste, handlePaste, pasteScope]);

  const acceptLabel = resolveAcceptLabel(
    accept,
    t('FileUploadDropzone.acceptImages')
  );

  return (
    <div ref={containerRef} className={cn('w-full', className)}>
      <Dropzone
        onDrop={handleFiles}
        multiple={multiple}
        accept={accept}
        maxSize={maxSize}
        disabled={disabled}
        className={cn(
          'w-full rounded-md',
          'outline-none ring-offset-bg-default transition-all',
          'focus-within:outline-hidden focus-within:ring-2 focus-within:ring-bg-emphasis/70 focus-within:ring-offset-2'
        )}
      >
        {({ isDragActive }) => (
          <div
            tabIndex={disabled ? -1 : 0}
            onPaste={
              enablePaste && pasteScope === 'container'
                ? handlePaste
                : undefined
            }
            className={cn(
              'border border-border-default rounded bg-bg-default text-center cursor-pointer transition-all duration-200 w-full outline-none',
              'hover:border-bg-inset',
              compact ? 'p-4' : 'p-6',
              isDragActive && 'border-bg-inset bg-bg-subtle',
              disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
            )}
          >
            <CloudUpload
              className={cn(
                'mx-auto text-fg-muted mb-3',
                compact ? 'size-6' : 'size-8'
              )}
            />
            <p
              className={cn(
                'font-medium text-fg-muted mb-1',
                compact ? 'text-sm' : 'text-md'
              )}
            >
              {title ?? t('FileUploadDropzone.dropFilesOrClickOrPaste')}
            </p>
            {(description ?? acceptLabel) && (
              <p className="text-sm text-fg-muted">
                {description ??
                  (acceptLabel
                    ? t('FileUploadDropzone.weAccept', { formats: acceptLabel })
                    : null)}
              </p>
            )}
          </div>
        )}
      </Dropzone>
    </div>
  );
}
